import * as XLSX from "xlsx";
import { getStateInfoFromGstin, getStateCodeFromName } from "./gstUtils";

/**
 * Formats a date string/timestamp into Indian GST Standard: DD-MMM-YYYY (e.g., 16-Oct-2026)
 */
export function formatGstDate(val) {
  if (!val) return "—";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "—";

  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * Resolves payment mode for GST filing ('G' for GST Bank, 'N' for Non-GST Bank)
 * Settings has bankDetails (Primary/GST) and bankDetails2 (Secondary/Non-GST)
 */
export function resolveInvoicePaymentMode(inv, settings = null) {
  if (inv?.paymentMode === "G" || inv?.paymentMode === "N") {
    return inv.paymentMode;
  }
  if (inv?.bankType === "gst" || inv?.bankMode === "G") return "G";
  if (inv?.bankType === "non-gst" || inv?.bankMode === "N") return "N";

  const invBank = inv?.companySnapshot?.bankDetails || inv?.bankDetails;
  const invAcc = (invBank?.accountNumber || "").trim().toLowerCase();
  const invBankName = (invBank?.bankName || "").trim().toLowerCase();

  const nonGstBank = settings?.bankDetails2;
  const nonGstAcc = (nonGstBank?.accountNumber || "").trim().toLowerCase();
  const nonGstName = (nonGstBank?.bankName || "").trim().toLowerCase();

  const gstBank = settings?.bankDetails;
  const gstAcc = (gstBank?.accountNumber || "").trim().toLowerCase();
  const gstName = (gstBank?.bankName || "").trim().toLowerCase();

  if (nonGstAcc && invAcc && invAcc === nonGstAcc) return "N";
  if (gstAcc && invAcc && invAcc === gstAcc) return "G";
  if (nonGstName && invBankName && invBankName === nonGstName) return "N";
  if (gstName && invBankName && invBankName === gstName) return "G";

  const customerGst = (inv?.clientSnapshot?.gstNumber || inv?.customerGst || inv?.gstNumber || "").trim();
  if (!customerGst && nonGstBank?.bankName) {
    return "N";
  }
  if (customerGst) {
    return "G";
  }

  return "N";
}

function resolveInvoiceServiceNames(items, inv) {
  if (Array.isArray(items) && items.length > 0) {
    const names = items.map((it) => it?.name).filter(Boolean);
    if (names.length > 0) return names.join(", ");
  }
  return inv?.productName || inv?.serviceName || "—";
}

function resolveInvoiceHsnCodes(items, inv) {
  if (Array.isArray(items) && items.length > 0) {
    const list = items
      .map((it) => (it?.sacHsn || it?.hsnCode || it?.hsn || "").trim())
      .filter(Boolean);
    if (list.length > 0) {
      return Array.from(new Set(list)).join(", ");
    }
  }
  const fallback = inv?.sacHsn || inv?.hsnCode || inv?.hsn || "998313";
  return String(fallback).trim();
}

function resolveInvoiceAmounts(inv, totalAmount) {
  let amountReceived = 0;
  if (inv?.amountPaid !== undefined) {
    amountReceived = Number(inv.amountPaid);
  } else if (inv?.paymentStatus === "paid") {
    amountReceived = totalAmount;
  }

  let balance = 0;
  if (inv?.balanceDue !== undefined) {
    balance = Number(inv.balanceDue);
  } else {
    balance = Math.max(0, totalAmount - amountReceived);
  }

  return { amountReceived, balance };
}

function deriveTaxValues(inv, taxable, gstRate, isInterState) {
  const halfRate = gstRate / 2;
  const calculatedCgst = isInterState ? 0 : (taxable * halfRate) / 100;
  const calculatedSgst = isInterState ? 0 : (taxable * halfRate) / 100;
  const calculatedIgst = isInterState ? (taxable * gstRate) / 100 : 0;

  const cgst = inv?.cgst !== undefined ? Number(inv.cgst) : calculatedCgst;
  const sgst = inv?.sgst !== undefined ? Number(inv.sgst) : calculatedSgst;
  const igst = inv?.igst !== undefined ? Number(inv.igst) : calculatedIgst;
  const totalTax = cgst + sgst + igst;
  const totalAmount = Number(inv?.total || Math.round(taxable + totalTax));

  return { cgst, sgst, igst, totalTax, totalAmount };
}

function resolvePlaceOfSupply(customerGst, inv) {
  const derivedFromGstin = customerGst ? getStateInfoFromGstin(customerGst) : { stateCode: "", stateName: "" };
  const rawStateName =
    derivedFromGstin.stateName ||
    inv?.clientSnapshot?.stateName ||
    inv?.clientSnapshot?.state ||
    inv?.companySnapshot?.stateName ||
    "Tamil Nadu";
  const rawStateCode =
    derivedFromGstin.stateCode ||
    inv?.clientSnapshot?.stateCode ||
    getStateCodeFromName(rawStateName) ||
    "33";

  return { placeOfSupply: `${rawStateCode}-${rawStateName}`, rawStateCode, rawStateName };
}

function applyPaidBasisCalculation(amounts, rawItems, calcBasis) {
  const { taxable, cgst, sgst, igst, totalTax, totalAmount, amountReceived } = amounts;
  if (calcBasis !== "paid") {
    return {
      taxableAmount: taxable,
      cgst,
      sgst,
      igst,
      totalTax,
      totalAmount,
      items: rawItems,
    };
  }

  const ratio = totalAmount > 0 ? Math.min(1, Math.max(0, amountReceived / totalAmount)) : 0;
  const finalCgst = Number((cgst * ratio).toFixed(2));
  const finalSgst = Number((sgst * ratio).toFixed(2));
  const finalIgst = Number((igst * ratio).toFixed(2));

  const items = rawItems.map((it) => {
    const taxableVal = Number(it?.taxable !== undefined ? it.taxable : (Number(it?.quantity || 1) * Number(it?.rate || 0)) || Number(it?.amount || 0));
    return {
      ...it,
      taxable: Number((taxableVal * ratio).toFixed(2)),
      cgst: Number((Number(it?.cgst || 0) * ratio).toFixed(2)),
      sgst: Number((Number(it?.sgst || 0) * ratio).toFixed(2)),
      igst: Number((Number(it?.igst || 0) * ratio).toFixed(2)),
    };
  });

  return {
    taxableAmount: Number((taxable * ratio).toFixed(2)),
    cgst: finalCgst,
    sgst: finalSgst,
    igst: finalIgst,
    totalTax: Number((finalCgst + finalSgst + finalIgst).toFixed(2)),
    totalAmount: amountReceived,
    items,
  };
}

/**
 * Normalizes an invoice record for GST compliance reporting
 */
export function normalizeGstInvoice(inv, settings = null, calcBasis = "total") {
  const customerGst = (inv?.clientSnapshot?.gstNumber || inv?.gstNumber || "").trim().toUpperCase();
  const isB2B = Boolean(customerGst && customerGst.length >= 10);
  const gstRate = Number(inv?.gstPercentage !== undefined ? inv.gstPercentage : 18);
  const isInterState = Boolean(inv?.isInterState);

  // Subtotal / Taxable amount
  let taxable = Number(inv?.subtotal || 0);
  if (!taxable && Number(inv?.total || 0) > 0) {
    const knownTax = Number(inv?.gstAmount || ((inv?.cgst || 0) + (inv?.sgst || 0) + (inv?.igst || 0)) || 0);
    taxable = Math.max(0, Number(inv?.total || 0) - knownTax);
  }

  const { cgst, sgst, igst, totalTax, totalAmount } = deriveTaxValues(inv, taxable, gstRate, isInterState);
  const { placeOfSupply, rawStateCode, rawStateName } = resolvePlaceOfSupply(customerGst, inv);

  const rawItems = Array.isArray(inv?.items) ? inv.items : [];
  const service = resolveInvoiceServiceNames(rawItems, inv);
  const sacHsn = resolveInvoiceHsnCodes(rawItems, inv);
  const { amountReceived, balance } = resolveInvoiceAmounts(inv, totalAmount);
  const paymentMode = resolveInvoicePaymentMode(inv, settings);

  const calculated = applyPaidBasisCalculation(
    { taxable, cgst, sgst, igst, totalTax, totalAmount, amountReceived },
    rawItems,
    calcBasis
  );

  return {
    _id:
      inv?._id ||
      inv?.id ||
      inv?.invoiceNumber ||
      (typeof window !== "undefined" && window.crypto?.randomUUID ? window.crypto.randomUUID() : `temp_${Date.now()}`),
    paymentMode,
    invoiceNumber: inv?.invoiceNumber || "INV-NEW",
    invoiceDate: inv?.invoiceDate || inv?.createdAt || new Date().toISOString(),
    customerName:
      (inv?.clientCompanyName ||
        inv?.clientSnapshot?.companyName ||
        inv?.companyName ||
        inv?.leadCompanyName ||
        inv?.clientSnapshot?.contactName ||
        inv?.contactName ||
        "").trim() || "Client Enterprise",
    clientCompanyName:
      (inv?.clientCompanyName ||
        inv?.clientSnapshot?.companyName ||
        inv?.companyName ||
        inv?.leadCompanyName ||
        "").trim() || "—",
    invoiceType: isB2B ? "B2B" : "B2C",
    customerGst,
    isB2B,
    isInterState,
    gstRate,
    amountReceived,
    balance,
    originalTotalAmount: totalAmount,
    originalTaxableAmount: taxable,
    taxableAmount: calculated.taxableAmount,
    cgst: calculated.cgst,
    sgst: calculated.sgst,
    igst: calculated.igst,
    totalTax: calculated.totalTax,
    totalAmount: calculated.totalAmount,
    calcBasis,
    placeOfSupply,
    stateCode: rawStateCode,
    stateName: rawStateName,
    paymentStatus: inv?.paymentStatus || "unpaid",
    paymentMethod: inv?.paymentMethod ? String(inv.paymentMethod).trim() : "",
    service,
    sacHsn,
    items: calculated.items,
  };
}

/**
 * Computes high-level GST return metric totals
 */
export function calculateGstFilingMetrics(records = []) {
  return records.reduce(
    (acc, r) => {
      acc.totalInvoices += 1;
      acc.totalTaxable += r.taxableAmount;
      acc.totalCgst += r.cgst;
      acc.totalSgst += r.sgst;
      acc.totalIgst += r.igst;
      acc.totalTax += r.totalTax;
      acc.totalGross += r.totalAmount;

      if (r.isB2B) {
        acc.b2bCount += 1;
        acc.b2bTaxable += r.taxableAmount;
        acc.b2bTax += r.totalTax;
        acc.b2bGross += r.totalAmount;
      } else {
        acc.b2cCount += 1;
        acc.b2cTaxable += r.taxableAmount;
        acc.b2cTax += r.totalTax;
        acc.b2cGross += r.totalAmount;
      }

      return acc;
    },
    {
      totalInvoices: 0,
      totalTaxable: 0,
      totalCgst: 0,
      totalSgst: 0,
      totalIgst: 0,
      totalTax: 0,
      totalGross: 0,
      b2bCount: 0,
      b2bTaxable: 0,
      b2bTax: 0,
      b2bGross: 0,
      b2cCount: 0,
      b2cTaxable: 0,
      b2cTax: 0,
      b2cGross: 0,
    }
  );
}

/**
 * Groups GST records by GST percentage slab
 */
export function groupInvoicesByGstRate(records = []) {
  const map = new Map();

  records.forEach((r) => {
    const rate = Number(r.gstRate || 0);
    if (!map.has(rate)) {
      map.set(rate, {
        rate,
        rateLabel: `${rate}%`,
        invoiceCount: 0,
        taxableAmount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        totalTax: 0,
        totalAmount: 0,
      });
    }

    const item = map.get(rate);
    item.invoiceCount += 1;
    item.taxableAmount += r.taxableAmount;
    item.cgst += r.cgst;
    item.sgst += r.sgst;
    item.igst += r.igst;
    item.totalTax += r.totalTax;
    item.totalAmount += r.totalAmount;
  });

  return Array.from(map.values()).sort((a, b) => b.rate - a.rate);
}

function accumulateHsnRecord(map, { hsn, qty, taxable, cgst, sgst, igst, totalTax }) {
  if (map.has(hsn)) {
    const existing = map.get(hsn);
    existing.totalCount += qty;
    existing.taxableValue += taxable;
    existing.cgstAmount += cgst;
    existing.sgstAmount += sgst;
    existing.igstAmount += igst;
    existing.totalTax += totalTax;
    existing.totalValue += (taxable + totalTax);
  } else {
    map.set(hsn, {
      hsnCode: hsn,
      totalCount: qty,
      taxableValue: taxable,
      cgstAmount: cgst,
      sgstAmount: sgst,
      igstAmount: igst,
      totalTax,
      totalValue: taxable + totalTax,
    });
  }
}

function processInvoiceItems(hsnMap, inv) {
  const isInvInterState = Boolean(inv?.isInterState);
  const invGstRate = Number(inv?.gstRate !== undefined ? inv.gstRate : 18);

  inv.items.forEach((it) => {
    const hsn = (it?.sacHsn || it?.hsnCode || it?.hsn || inv?.sacHsn || "998313").trim();
    const qty = Number(it?.quantity || 1);
    const taxable = Number(it?.taxable !== undefined ? it.taxable : (qty * Number(it?.rate || 0)) || Number(it?.amount || 0));
    const itemGstRate = Number(it?.gstPercentage !== undefined ? it.gstPercentage : invGstRate);
    const itemInterState = Boolean(it?.isInterState !== undefined ? it.isInterState : isInvInterState);

    const calculatedCgst = itemInterState ? 0 : (taxable * (itemGstRate / 2)) / 100;
    const calculatedSgst = itemInterState ? 0 : (taxable * (itemGstRate / 2)) / 100;
    const calculatedIgst = itemInterState ? (taxable * itemGstRate) / 100 : 0;

    const cgst = Number(it?.cgst !== undefined ? it.cgst : calculatedCgst);
    const sgst = Number(it?.sgst !== undefined ? it.sgst : calculatedSgst);
    const igst = Number(it?.igst !== undefined ? it.igst : calculatedIgst);
    const totalTax = cgst + sgst + igst;

    accumulateHsnRecord(hsnMap, { hsn, qty, taxable, cgst, sgst, igst, totalTax });
  });
}

/**
 * Groups invoices into Table 12 HSN/SAC summary
 */
export function groupInvoicesByHsn(invoices = []) {
  const hsnMap = new Map();

  invoices.forEach((inv) => {
    if (Array.isArray(inv?.items) && inv.items.length > 0) {
      processInvoiceItems(hsnMap, inv);
    } else {
      const hsn = (inv?.sacHsn || inv?.hsnCode || "998313").trim();
      const qty = 1;
      const taxable = Number(inv?.taxableAmount || 0);
      const cgst = Number(inv?.cgst || 0);
      const sgst = Number(inv?.sgst || 0);
      const igst = Number(inv?.igst || 0);
      const totalTax = inv?.totalTax !== undefined ? Number(inv.totalTax) : cgst + sgst + igst;

      accumulateHsnRecord(hsnMap, { hsn, qty, taxable, cgst, sgst, igst, totalTax });
    }
  });

  return Array.from(hsnMap.values()).sort((a, b) => b.taxableValue - a.taxableValue);
}

/**
 * Escapes fields for CSV output according to RFC 4180
 */
function escapeCsvValue(val) {
  if (val === null || val === undefined) return '""';
  const str = String(val);
  if (str.includes(",") || str.includes('"') || str.includes("\n") || str.includes("\r")) {
    return `"${str.replaceAll('"', '""')}"`;
  }
  return `"${str}"`;
}

/**
 * Generates Table 12 HSN Wise Summary CSV string
 */
export function generateHsnSummaryCsvContent(hsnRows = []) {
  const headers = [
    "HSN",
    "Total HSN",
    "Taxable Value",
    "IGST",
    "CGST",
    "SGST",
    "Total Tax",
    "Total Value",
  ];

  let sumCount = 0;
  let sumTaxable = 0;
  let sumIgst = 0;
  let sumCgst = 0;
  let sumSgst = 0;
  let sumTotalTax = 0;
  let sumTotalValue = 0;

  const rows = hsnRows.map((row) => {
    const count = Number(row.totalCount || 0);
    const taxable = Number(row.taxableValue || 0);
    const igst = Number(row.igstAmount || 0);
    const cgst = Number(row.cgstAmount || 0);
    const sgst = Number(row.sgstAmount || 0);
    const tax = Number(row.totalTax !== undefined ? row.totalTax : cgst + sgst + igst);
    const val = Number(row.totalValue !== undefined ? row.totalValue : taxable + tax);

    sumCount += count;
    sumTaxable += taxable;
    sumIgst += igst;
    sumCgst += cgst;
    sumSgst += sgst;
    sumTotalTax += tax;
    sumTotalValue += val;

    return [
      escapeCsvValue(row.hsnCode || "998313"),
      count,
      taxable.toFixed(2),
      igst.toFixed(2),
      cgst.toFixed(2),
      sgst.toFixed(2),
      tax.toFixed(2),
      val.toFixed(2),
    ].join(",");
  });

  const totalRow = [
    escapeCsvValue(">>> TOTAL <<<"),
    sumCount,
    escapeCsvValue(`*** ${sumTaxable.toFixed(2)} ***`),
    sumIgst.toFixed(2),
    sumCgst.toFixed(2),
    sumSgst.toFixed(2),
    sumTotalTax.toFixed(2),
    sumTotalValue.toFixed(2),
  ].join(",");

  return [headers.join(","), ...rows, totalRow].join("\r\n");
}

function buildInvoiceTableTotalRow(records = []) {
  let sumAmountReceived = 0;
  let sumBalance = 0;
  let sumTotalAmount = 0;
  let sumTaxableAmount = 0;
  let sumIgst = 0;
  let sumCgst = 0;
  let sumSgst = 0;

  records.forEach((r) => {
    sumAmountReceived += Number(r.amountReceived || 0);
    sumBalance += Number(r.balance || 0);
    sumTotalAmount += Number(r.totalAmount || 0);
    sumTaxableAmount += Number(r.taxableAmount || 0);
    sumIgst += Number(r.igst || 0);
    sumCgst += Number(r.cgst || 0);
    sumSgst += Number(r.sgst || 0);
  });

  return [
    escapeCsvValue(">>> TOTAL <<<"),
    "",
    "",
    "",
    "",
    "",
    "",
    sumAmountReceived.toFixed(2),
    sumBalance.toFixed(2),
    sumTotalAmount.toFixed(2),
    escapeCsvValue(`*** ${sumTaxableAmount.toFixed(2)} ***`),
    sumIgst.toFixed(2),
    sumCgst.toFixed(2),
    sumSgst.toFixed(2),
    "",
    "",
    "",
  ].join(",");
}

/**
 * Generates official spreadsheet and GST compliant CSV string including Table 12 HSN split-up
 */
export function generateGstr1CsvContent(records = [], hsnRows = null) {
  const headers = [
    "Payment mode",
    "Payment Method",
    "B2B / B2C",
    "GST Number",
    "Client",
    "Invoice Number",
    "Date",
    "Amount Received",
    "Balance",
    "Total Invoice Value",
    "Taxable Value",
    "IGST",
    "CGST",
    "SGST",
    "Status",
    "Service",
    "HSN",
  ];

  const rows = records.map((r) => [
    escapeCsvValue(r.paymentMode || "N"),
    escapeCsvValue(r.paymentMethod || "—"),
    escapeCsvValue(r.isB2B ? "B2B" : "B2C"),
    escapeCsvValue(r.customerGst || "—"),
    escapeCsvValue(r.customerName),
    escapeCsvValue(r.invoiceNumber),
    escapeCsvValue(formatGstDate(r.invoiceDate)),
    Number(r.amountReceived || 0).toFixed(2),
    Number(r.balance || 0).toFixed(2),
    Number(r.totalAmount || 0).toFixed(2),
    Number(r.taxableAmount || 0).toFixed(2),
    Number(r.igst || 0).toFixed(2),
    Number(r.cgst || 0).toFixed(2),
    Number(r.sgst || 0).toFixed(2),
    escapeCsvValue(r.paymentStatus || "unpaid"),
    escapeCsvValue(r.service || "—"),
    escapeCsvValue(r.sacHsn || "998313"),
  ]);

  const csvSections = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
    buildInvoiceTableTotalRow(records),
  ];

  const resolvedHsnRows = Array.isArray(hsnRows) && hsnRows.length > 0
    ? hsnRows
    : groupInvoicesByHsn(records);

  if (Array.isArray(resolvedHsnRows) && resolvedHsnRows.length > 0) {
    csvSections.push(
      "",
      '"HSN / SAC SUMMARY (TABLE 12)"',
      generateHsnSummaryCsvContent(resolvedHsnRows)
    );
  }

  return csvSections.join("\r\n");
}

/**
 * Downloads GSTR-1 CSV file to client disk (including Table 12 HSN split-up)
 */
export function exportGstr1CsvFile(records = [], hsnRows = [], periodLabel = "All", calcBasis = "total") {
  let resolvedHsn = hsnRows;
  let resolvedPeriod = periodLabel;
  if (typeof hsnRows === "string") {
    resolvedPeriod = hsnRows;
    resolvedHsn = groupInvoicesByHsn(records);
  } else if (!hsnRows || hsnRows.length === 0) {
    resolvedHsn = groupInvoicesByHsn(records);
  }

  const csvContent = generateGstr1CsvContent(records, resolvedHsn);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const sanitizedPeriod = resolvedPeriod.replaceAll(/[^a-zA-Z0-9_-]/g, "_");
  const basisSuffix = calcBasis === "paid" ? "_Paid_Basis" : "";
  const fileName = `GSTR1_Filing_Report_${sanitizedPeriod}${basisSuffix}.csv`;

  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Downloads Invoices Only CSV file (without Table 12 HSN summary)
 */
export function exportInvoicesCsvFile(records = [], periodLabel = "All", calcBasis = "total") {
  const headers = [
    "Payment mode",
    "Payment Method",
    "B2B / B2C",
    "GST Number",
    "Client",
    "Invoice Number",
    "Date",
    "Amount Received",
    "Balance",
    "Total Invoice Value",
    "Taxable Value",
    "IGST",
    "CGST",
    "SGST",
    "Status",
    "Service",
    "HSN",
  ];

  const rows = records.map((r) => [
    escapeCsvValue(r.paymentMode || "N"),
    escapeCsvValue(r.paymentMethod || "—"),
    escapeCsvValue(r.isB2B ? "B2B" : "B2C"),
    escapeCsvValue(r.customerGst || "—"),
    escapeCsvValue(r.customerName),
    escapeCsvValue(r.invoiceNumber),
    escapeCsvValue(formatGstDate(r.invoiceDate)),
    Number(r.amountReceived || 0).toFixed(2),
    Number(r.balance || 0).toFixed(2),
    Number(r.totalAmount || 0).toFixed(2),
    Number(r.taxableAmount || 0).toFixed(2),
    Number(r.igst || 0).toFixed(2),
    Number(r.cgst || 0).toFixed(2),
    Number(r.sgst || 0).toFixed(2),
    escapeCsvValue(r.paymentStatus || "unpaid"),
    escapeCsvValue(r.service || "—"),
    escapeCsvValue(r.sacHsn || "998313"),
  ]);

  const csvSections = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
    buildInvoiceTableTotalRow(records),
  ];

  const csvContent = csvSections.join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const sanitizedPeriod = periodLabel.replaceAll(/[^a-zA-Z0-9_-]/g, "_");
  const basisSuffix = calcBasis === "paid" ? "_Paid_Basis" : "";
  const fileName = `Invoices_Report_${sanitizedPeriod}${basisSuffix}.csv`;

  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Downloads dedicated Table 12 HSN Summary CSV file
 */
export function exportHsnSummaryCsvFile(hsnRows = [], periodLabel = "All") {
  const csvContent = generateHsnSummaryCsvContent(hsnRows);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const sanitizedPeriod = periodLabel.replaceAll(/[^a-zA-Z0-9_-]/g, "_");
  const fileName = `GSTR1_HSN_Summary_${sanitizedPeriod}.csv`;

  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Downloads comprehensive GSTR-1 Multi-sheet Excel workbook (.xlsx)
 */
export function exportGstr1ExcelFile(records = [], metrics = {}, rateRows = [], hsnRows = [], periodLabel = "All") {
  const wb = XLSX.utils.book_new();

  // 1. Full Invoice Spreadsheet Register (Exact order requested)
  const registerData = records.map((r) => ({
    "Payment mode": r.paymentMode || "N",
    "Payment Method": r.paymentMethod || "—",
    "B2B / B2C": r.isB2B ? "B2B" : "B2C",
    "GST Number": r.customerGst || "—",
    Client: r.customerName || "—",
    "Invoice Number": r.invoiceNumber,
    Date: formatGstDate(r.invoiceDate),
    "Amount Received": Number(Number(r.amountReceived || 0).toFixed(2)),
    Balance: Number(Number(r.balance || 0).toFixed(2)),
    "Total Invoice Value": Number(Number(r.totalAmount || 0).toFixed(2)),
    "Taxable Value": Number(Number(r.taxableAmount || 0).toFixed(2)),
    IGST: Number(Number(r.igst || 0).toFixed(2)),
    CGST: Number(Number(r.cgst || 0).toFixed(2)),
    SGST: Number(Number(r.sgst || 0).toFixed(2)),
    Status: r.paymentStatus || "unpaid",
    Service: r.service || "—",
    HSN: r.sacHsn || "998313",
  }));
  const wsRegister = XLSX.utils.json_to_sheet(registerData);
  XLSX.utils.book_append_sheet(wb, wsRegister, "Invoice_Register");

  // 2. Overall Summary Sheet
  const summaryData = [
    { "Metric Description": "GST Compliance Filing Period", Value: periodLabel },
    { "Metric Description": "Total Invoices Filed", Value: metrics.totalInvoices || 0 },
    { "Metric Description": "Gross Turnover (INR)", Value: Number(metrics.totalGross || 0).toFixed(2) },
    { "Metric Description": "Total Taxable Value (INR)", Value: Number(metrics.totalTaxable || 0).toFixed(2) },
    { "Metric Description": "Total IGST (Integrated Tax) (INR)", Value: Number(metrics.totalIgst || 0).toFixed(2) },
    { "Metric Description": "Total CGST (Central Tax) (INR)", Value: Number(metrics.totalCgst || 0).toFixed(2) },
    { "Metric Description": "Total SGST (State Tax) (INR)", Value: Number(metrics.totalSgst || 0).toFixed(2) },
    { "Metric Description": "Total GST Liability (INR)", Value: Number(metrics.totalTax || 0).toFixed(2) },
    { "Metric Description": "B2B Registered Invoices Count", Value: metrics.b2bCount || 0 },
    { "Metric Description": "B2B Taxable Value (INR)", Value: Number(metrics.b2bTaxable || 0).toFixed(2) },
    { "Metric Description": "B2B GST Tax (INR)", Value: Number(metrics.b2bTax || 0).toFixed(2) },
    { "Metric Description": "B2C Consumer Invoices Count", Value: metrics.b2cCount || 0 },
    { "Metric Description": "B2C Taxable Value (INR)", Value: Number(metrics.b2cTaxable || 0).toFixed(2) },
    { "Metric Description": "B2C GST Tax (INR)", Value: Number(metrics.b2cTax || 0).toFixed(2) },
  ];
  const wsSummary = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

  // 2. B2B Invoices Sheet
  const b2bRecords = records.filter((r) => r.isB2B);
  const b2bData = b2bRecords.map((r) => ({
    "GSTIN/UIN of Recipient": r.customerGst,
    "Receiver Name": r.customerName,
    "Invoice Number": r.invoiceNumber,
    "Invoice Date": formatGstDate(r.invoiceDate),
    "Invoice Value": Number(r.totalAmount.toFixed(2)),
    "Place Of Supply": r.placeOfSupply,
    "Reverse Charge": "N",
    "Invoice Type": "Regular",
    "Rate (%)": r.gstRate,
    "Taxable Value": Number(r.taxableAmount.toFixed(2)),
    "Integrated Tax (IGST)": Number(r.igst.toFixed(2)),
    "Central Tax (CGST)": Number(r.cgst.toFixed(2)),
    "State Tax (SGST)": Number(r.sgst.toFixed(2)),
    "Cess Amount": 0,
  }));
  const wsB2B = XLSX.utils.json_to_sheet(b2bData);
  XLSX.utils.book_append_sheet(wb, wsB2B, "GSTR1_B2B");

  // 3. B2C Invoices Sheet
  const b2cRecords = records.filter((r) => !r.isB2B);
  const b2cData = b2cRecords.map((r) => ({
    "Receiver Name": r.customerName,
    "Invoice Number": r.invoiceNumber,
    "Invoice Date": formatGstDate(r.invoiceDate),
    "Invoice Value": Number(r.totalAmount.toFixed(2)),
    "Place Of Supply": r.placeOfSupply,
    "Rate (%)": r.gstRate,
    "Taxable Value": Number(r.taxableAmount.toFixed(2)),
    "Integrated Tax (IGST)": Number(r.igst.toFixed(2)),
    "Central Tax (CGST)": Number(r.cgst.toFixed(2)),
    "State Tax (SGST)": Number(r.sgst.toFixed(2)),
  }));
  const wsB2C = XLSX.utils.json_to_sheet(b2cData);
  XLSX.utils.book_append_sheet(wb, wsB2C, "GSTR1_B2C");

  // 4. Rate-wise Summary Sheet
  const rateData = rateRows.map((row) => ({
    "Rate Slab": row.rateLabel,
    "Invoices Count": row.invoiceCount,
    "Taxable Value": Number(row.taxableAmount.toFixed(2)),
    "Central Tax (CGST)": Number(row.cgst.toFixed(2)),
    "State Tax (SGST)": Number(row.sgst.toFixed(2)),
    "Integrated Tax (IGST)": Number(row.igst.toFixed(2)),
    "Total Tax": Number(row.totalTax.toFixed(2)),
    "Total Amount": Number(row.totalAmount.toFixed(2)),
  }));
  const wsRate = XLSX.utils.json_to_sheet(rateData);
  XLSX.utils.book_append_sheet(wb, wsRate, "Rate_Summary");

  // 5. HSN Summary Sheet (Table 12)
  if (Array.isArray(hsnRows) && hsnRows.length > 0) {
    const hsnData = hsnRows.map((row) => ({
      HSN: row.hsnCode,
      "Total HSN": row.totalCount,
      "Taxable Value": Number(row.taxableValue.toFixed(2)),
      IGST: Number(row.igstAmount.toFixed(2)),
      CGST: Number(row.cgstAmount.toFixed(2)),
      SGST: Number(row.sgstAmount.toFixed(2)),
      "Total Tax": Number(row.totalTax.toFixed(2)),
      "Total Value": Number(row.totalValue.toFixed(2)),
    }));
    const wsHsn = XLSX.utils.json_to_sheet(hsnData);
    XLSX.utils.book_append_sheet(wb, wsHsn, "HSN_Summary");
  }

  const sanitizedPeriod = periodLabel.replaceAll(/[^a-zA-Z0-9_-]/g, "_");
  XLSX.writeFile(wb, `GSTR1_Filing_Report_${sanitizedPeriod}.xlsx`);
}

/**
 * Calculates current and previous Indian Financial Year metadata (Apr 01 - Mar 31)
 */
export function getIndianFinancialYearInfo(refDate = new Date()) {
  const currentYear = refDate.getFullYear();
  const currentMonth = refDate.getMonth(); // 0 = Jan ... 11 = Dec

  const fyStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
  const fyEndYear = fyStartYear + 1;
  const shortEnd = String(fyEndYear).slice(-2);
  const fullLabel = `Financial Year (${fyStartYear}-${shortEnd})`;
  const shortLabel = `FY ${fyStartYear}-${shortEnd}`;

  const prevFyStart = fyStartYear - 1;
  const prevFyEnd = fyStartYear;
  const prevShortEnd = String(prevFyEnd).slice(-2);
  const prevFullLabel = `Last Financial Year (${prevFyStart}-${prevShortEnd})`;

  let currentQuarterIndex = 4;
  if (currentMonth >= 3 && currentMonth <= 5) {
    currentQuarterIndex = 1;
  } else if (currentMonth >= 6 && currentMonth <= 8) {
    currentQuarterIndex = 2;
  } else if (currentMonth >= 9 && currentMonth <= 11) {
    currentQuarterIndex = 3;
  }

  const lastQuarterIndex = currentQuarterIndex === 1 ? 4 : currentQuarterIndex - 1;
  const lastQuarterFyStart = currentQuarterIndex === 1 ? prevFyStart : fyStartYear;

  return {
    fyStartYear,
    fyEndYear,
    shortEnd,
    fullLabel,
    shortLabel,
    prevFyStart,
    prevFyEnd,
    prevFullLabel,
    currentQuarterIndex,
    lastQuarterIndex,
    lastQuarterFyStart,
    dateFrom: `${fyStartYear}-04-01`,
    dateTo: refDate.toISOString().split("T")[0],
  };
}

export const QUARTER_KEYS = [
  "this_quarter",
  "last_quarter",
  "q1",
  "q2",
  "q3",
  "q4",
  "last_year_q1",
  "last_year_q2",
  "last_year_q3",
  "last_year_q4",
];

export const HALF_YEAR_KEYS = [
  "h1",
  "h2",
  "last_year_h1",
  "last_year_h2",
];

export const QUARTER_SECTIONS = [
  {
    title: "Current Year",
    items: [
      { key: "this_quarter", label: "This Quarter" },
      { key: "last_quarter", label: "Last Quarter" },
      { key: "q1", label: "Q1 (Apr – Jun)" },
      { key: "q2", label: "Q2 (Jul – Sep)" },
      { key: "q3", label: "Q3 (Oct – Dec)" },
      { key: "q4", label: "Q4 (Jan – Mar)" },
    ],
  },
  {
    title: "Last Financial Year",
    items: [
      { key: "last_year_q1", label: "Last Year Q1 (Apr – Jun)" },
      { key: "last_year_q2", label: "Last Year Q2 (Jul – Sep)" },
      { key: "last_year_q3", label: "Last Year Q3 (Oct – Dec)" },
      { key: "last_year_q4", label: "Last Year Q4 (Jan – Mar)" },
    ],
  },
];

export const HALF_YEAR_SECTIONS = [
  {
    title: "Current Year",
    items: [
      { key: "h1", label: "1st Half (Apr – Sep)" },
      { key: "h2", label: "2nd Half (Oct – Mar)" },
    ],
  },
  {
    title: "Last Financial Year",
    items: [
      { key: "last_year_h1", label: "Last Year 1st Half (Apr – Sep)" },
      { key: "last_year_h2", label: "Last Year 2nd Half (Oct – Mar)" },
    ],
  },
];

function resolveQuarterDates(fyStartYear, qIndex, prefix) {
  const fyEndYear = fyStartYear + 1;
  const map = {
    1: { dateFrom: `${fyStartYear}-04-01`, dateTo: `${fyStartYear}-06-30`, name: "Q1" },
    2: { dateFrom: `${fyStartYear}-07-01`, dateTo: `${fyStartYear}-09-30`, name: "Q2" },
    3: { dateFrom: `${fyStartYear}-10-01`, dateTo: `${fyStartYear}-12-31`, name: "Q3" },
    4: { dateFrom: `${fyEndYear}-01-01`, dateTo: `${fyEndYear}-03-31`, name: "Q4" },
  };
  const target = map[qIndex] || map[1];
  return {
    dateFrom: target.dateFrom,
    dateTo: target.dateTo,
    label: `${prefix} (${target.name})`,
  };
}

function resolveMonthRange(refDate, monthOffset) {
  const targetMonth = refDate.getMonth() + monthOffset;
  const start = new Date(refDate.getFullYear(), targetMonth, 1);
  const end = new Date(refDate.getFullYear(), targetMonth + 1, 0);
  return {
    dateFrom: start.toISOString().split("T")[0],
    dateTo: end.toISOString().split("T")[0],
    label: start.toLocaleDateString("en-IN", { month: "short", year: "numeric" }),
  };
}

function resolveQuarterPeriod(periodKey, fyInfo) {
  const quarterMap = {
    this_quarter: () => resolveQuarterDates(fyInfo.fyStartYear, fyInfo.currentQuarterIndex, "This Quarter"),
    last_quarter: () => resolveQuarterDates(fyInfo.lastQuarterFyStart, fyInfo.lastQuarterIndex, "Last Quarter"),
    q1: () => ({ dateFrom: `${fyInfo.fyStartYear}-04-01`, dateTo: `${fyInfo.fyStartYear}-06-30`, label: "Q1 (Apr – Jun)" }),
    q2: () => ({ dateFrom: `${fyInfo.fyStartYear}-07-01`, dateTo: `${fyInfo.fyStartYear}-09-30`, label: "Q2 (Jul – Sep)" }),
    q3: () => ({ dateFrom: `${fyInfo.fyStartYear}-10-01`, dateTo: `${fyInfo.fyStartYear}-12-31`, label: "Q3 (Oct – Dec)" }),
    q4: () => ({ dateFrom: `${fyInfo.fyEndYear}-01-01`, dateTo: `${fyInfo.fyEndYear}-03-31`, label: "Q4 (Jan – Mar)" }),
    last_year_q1: () => ({ dateFrom: `${fyInfo.prevFyStart}-04-01`, dateTo: `${fyInfo.prevFyStart}-06-30`, label: "Last Year Q1 (Apr – Jun)" }),
    last_year_q2: () => ({ dateFrom: `${fyInfo.prevFyStart}-07-01`, dateTo: `${fyInfo.prevFyStart}-09-30`, label: "Last Year Q2 (Jul – Sep)" }),
    last_year_q3: () => ({ dateFrom: `${fyInfo.prevFyStart}-10-01`, dateTo: `${fyInfo.prevFyStart}-12-31`, label: "Last Year Q3 (Oct – Dec)" }),
    last_year_q4: () => ({ dateFrom: `${fyInfo.prevFyEnd}-01-01`, dateTo: `${fyInfo.prevFyEnd}-03-31`, label: "Last Year Q4 (Jan – Mar)" }),
  };

  const resolver = quarterMap[periodKey];
  return resolver ? resolver() : null;
}

function resolveHalfYearPeriod(periodKey, fyInfo) {
  const halfYearMap = {
    h1: { dateFrom: `${fyInfo.fyStartYear}-04-01`, dateTo: `${fyInfo.fyStartYear}-09-30`, label: "1st Half (Apr – Sep)" },
    h2: { dateFrom: `${fyInfo.fyStartYear}-10-01`, dateTo: `${fyInfo.fyEndYear}-03-31`, label: "2nd Half (Oct – Mar)" },
    last_year_h1: { dateFrom: `${fyInfo.prevFyStart}-04-01`, dateTo: `${fyInfo.prevFyStart}-09-30`, label: "Last Year 1st Half (Apr – Sep)" },
    last_year_h2: { dateFrom: `${fyInfo.prevFyStart}-10-01`, dateTo: `${fyInfo.prevFyEnd}-03-31`, label: "Last Year 2nd Half (Oct – Mar)" },
  };

  return halfYearMap[periodKey] || null;
}

/**
 * Resolves Date Range for standard Indian tax compliance periods
 */
export function resolvePeriodDates(periodKey, refDate = new Date()) {
  const fyInfo = getIndianFinancialYearInfo(refDate);

  if (periodKey === "this_month") {
    return resolveMonthRange(refDate, 0);
  }
  if (periodKey === "last_month") {
    return resolveMonthRange(refDate, -1);
  }

  const quarterResult = resolveQuarterPeriod(periodKey, fyInfo);
  if (quarterResult) return quarterResult;

  const halfYearResult = resolveHalfYearPeriod(periodKey, fyInfo);
  if (halfYearResult) return halfYearResult;

  if (periodKey === "fy_ytd") {
    return { dateFrom: `${fyInfo.fyStartYear}-04-01`, dateTo: `${fyInfo.fyEndYear}-03-31`, label: fyInfo.fullLabel };
  }
  if (periodKey === "fy_last") {
    return { dateFrom: `${fyInfo.prevFyStart}-04-01`, dateTo: `${fyInfo.prevFyEnd}-03-31`, label: fyInfo.prevFullLabel };
  }

  return { dateFrom: null, dateTo: null, label: "All Dates" };
}


